import { contexts, Fieldset, View, WaitScreen } from 'lib_ui-primitives';
import { constants, metadata, uiRuntimeConfig } from 'lib_ui-services';
import propTypes from 'prop-types';
import { isValidElement, createElement as rc, useContext, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import useEventSink from '../../hooks/useEventSink';
import useFormMachine from '../../hooks/useFormMachine';
import useIsLoading from '../../hooks/useIsLoading';
import FilterInterdependencyBoundary from '../contextProviders/FilterInterdependencyBoundary';
import { ActiveToggle, FormHeaderButton } from '../formButton';
import MenuButton from '../formElement/MenuButton';
import { MenuDivider, MenuItem } from '../menuSelection';
import { arrangeButtons, getFormElements, getOtherElements } from './formComponentSelectors';
import {
    AssetNo,
    ButtonBar,
    ErrorSection,
    ErrorText,
    FormBody,
    FormFooter,
    FormHeader,
    GreedyOtherElements,
    HeaderButtons,
    InactiveBanner,
    InactiveBannerText,
    InactiveBannerTitle,
    InactiveOverlay,
    Loading,
    MobileTitle,
    ScrollableFormBody,
    StyledForm
} from './styles';
import Title from './Title';

const { getPrettyRelationName } = metadata;

// @ts-ignore
/** @typedef {import('../../hooks/useFormMachine').FormContext} FormContext */

const defaultErrors = { field: {}, form: [] };

/**
 * @typedef {Object} Props
 * @property {boolean} centerFields
 * @property {Object} hNode
 * @property {boolean} heightGreedy
 * @property {import('react').FormEventHandler} [onSubmit]
 * @property {string} title
 */

/** @typedef {import('react').ReactNode} ReactNode */

/**
 * The form has two primary types of layout:
 * @type {import('react').FC<Props>}
 */
const FormGL2025 = props => {
    const { mobile, native } = useTheme();
    const dialogHasTitle = useContext(contexts.DialogTitleContext);
    const {
        centerFields,
        heightGreedy,
        // @ts-ignore
        children,
        // @ts-ignore
        hNode: { id, namespace, relation, retainParentContext = false, allowRecordDeactivation = false },
        // @ts-ignore
        hNode,
        // @ts-ignore
        onSubmit,
        // @ts-ignore
        errors = defaultErrors,
        // @ts-ignore
        popErrorNotifications = !mobile
    } = props || { hNode: {} };

    /** @type {FormContext} */
    const formContext = useFormMachine(hNode);
    const isLoading = useIsLoading(namespace, relation);

    const [, publish] = useEventSink();
    const [formErrors, setFormErrors] = useState([]);
    const [fieldErrors, setFieldErrors] = useState([]);

    // Combine errors passed from parents with any parents passed with the formContext
    // and set a stateful value so that these rendered or the user can be notified
    // (if popErrorNotifications is true).
    useEffect(() => {
        let _formErrors = [...formContext.formErrors, ...errors.form];
        _formErrors = _formErrors.map(e => e.message || e);
        setFormErrors(_formErrors);
        const fieldErrors = aggregateErrors(formContext.errorsByPath);
        setFieldErrors(fieldErrors);
        if (_formErrors.length > 0 && !fieldErrors.length) {
            setFieldErrors(_formErrors);
        }
    }, [formContext.formErrors, errors, formContext.errorsByPath, publish]);

    // If there are field level errors, publish them as a (toast) notification
    useEffect(() => {
        if (popErrorNotifications && fieldErrors.length > 0) {
            fieldErrors.forEach(message =>
                publish(
                    {
                        variant: 'error',
                        type: constants.notificationTypes.TOAST,
                        message,
                        timeout: uiRuntimeConfig.getNotificationTimeoutFormError()
                    },
                    // @ts-ignore
                    { verb: 'pop', namespace: 'application', relation: 'notification' }
                )
            );
        }
    }, [fieldErrors, publish, popErrorNotifications]);

    // // If there are form level errors, publish them as a (toast) notification
    // useEffect(() => {
    //     if (popErrorNotifications) {
    //         formErrors.forEach(message => {
    //             publish(
    //                 {
    //                     variant: 'error',
    //                     type: constants.notificationTypes.TOAST,
    //                     message,
    //                     timeout: uiRuntimeConfig.getNotificationTimeoutFormError()
    //                 },
    //                 // @ts-ignore
    //                 { verb: 'pop', namespace: 'application', relation: 'notification' }
    //             );
    //         });
    //     }
    // }, [formErrors, publish, popErrorNotifications]);

    if (isLoading) {
        return rc(Loading, null, 'Loading...');
    }

    /**
     * Separate out the types of components
     *
     * - formElements (typically inputs)
     * will be rendered first inside a fieldset.
     * (This may need to be altered to include things like actionElements if any
     * forms contain those, but right now they are included in 'otherElements'.)
     *
     * - Other elements (typically layout components like tabs) will go next and
     * will be greedy with space, taking up as much as is available after the
     * components render.
     *
     * - Last, the form footer will contain the formButtons that are FORM_BUTTON_ACTIONS.
     * - Any other formButtons will be rendered in the header menu.
     * */

    const formElements = getFormElements(children);
    const otherElements = getOtherElements(children);
    const otherElementsExist = otherElements != null && (otherElements.length > 0 || !Array.isArray(otherElements));
    const [formButtons, menuButtons, cancelButton, headerButtons] = arrangeButtons(children, allowRecordDeactivation);
    const record = formContext.newRecord || formContext.record || {};
    const isInactive = record.active === false;
    const formButtonsExist = Array.isArray(formButtons) ? formButtons.length > 0 : formButtons;
    const showFooter = formButtonsExist || formErrors.length > 0;

    const handleSubmit = event => {
        event.preventDefault();
        if (onSubmit) {
            onSubmit(event);
        }
    };

    // @ts-ignore
    return rc(
        FilterInterdependencyBoundary,
        { name: 'form', restoreSelectionsOnRemount: true, retainParentContext },
        rc(
            contexts.FormContext.Provider,
            { value: formContext },
            rc(
                StyledForm,
                { autoComplete: 'off', id, onSubmit: handleSubmit, heightGreedy },
                // WaitScreen stays inside StyledForm: StyledForm is `position: relative`
                // (form/styles.js), which keeps its absolutely positioned overlay scoped here.
                rc(
                    WaitScreen,
                    { id: `waitScreen-${id}`, waiting: formContext.submitting },
                    rc(
                        FormHeader,
                        null,
                        createTitleElement(hNode, record, mobile, dialogHasTitle),
                        createHeaderButtons(id, menuButtons, cancelButton, headerButtons)
                    ),
                    rc(
                        // On native mobile with no tab/other-element sections, use a
                        // ScrollView-based form body so plain form fields can scroll
                        // when they exceed the viewport. When tabs (otherElements) are
                        // present, use a plain View instead — the inner FlatList already
                        // handles its own scrolling, and wrapping tabs in an outer
                        // ScrollView causes the Get More toolbar to scroll off-screen.
                        native && !otherElementsExist ? ScrollableFormBody : FormBody,
                        native && !otherElementsExist ? { contentContainerStyle: { flexGrow: 1 } } : null,
                        isInactive && rc(InactiveOverlay),
                        rc(Fieldset, { centerFields, greedy: !otherElementsExist }, formElements),
                        otherElementsExist && rc(GreedyOtherElements, null, otherElements)
                    ),
                    showFooter &&
                        rc(
                            FormFooter,
                            { isInactive },
                            rc(
                                ErrorSection,
                                null,
                                formErrors.map((err, i) => rc(ErrorText, { key: `formError${i}` }, err))
                            ),
                            isInactive
                                ? rc(
                                      InactiveBanner,
                                      null,
                                      rc(
                                          InactiveBannerTitle,
                                          null,
                                          `${getPrettyRelationName(namespace, relation) || 'Record'} disabled`
                                      ),
                                      rc(InactiveBannerText, null, 'Use the menu to toggle activation')
                                  )
                                : rc(ButtonBar, null, formButtons)
                        )
                )
            )
        )
    );
};

function createHeaderButtons(id, menuButtons, cancelButton, headerButtons = []) {
    const style = {
        marginLeft: 6,
        marginRight: 6
    };

    const menuItems =
        menuButtons &&
        menuButtons.map((button, i) => {
            if (!isValidElement(button)) {
                return button;
            }

            switch (button.type) {
                case MenuItem:
                case ActiveToggle:
                case MenuDivider:
                    return button;
            }

            /** @type {any} */
            const buttonProps = button.props || {};
            const bHNode = buttonProps.hNode;
            const otherProps = { ...buttonProps };
            delete otherProps.hNode;

            return rc(MenuItem, {
                key: button.key || `header-menu-button-${id}-${i}`,
                ...otherProps,
                hNode: bHNode
            });
        });

    const menuButton =
        menuButtons &&
        rc(
            MenuButton,
            {
                hNode: {
                    id: `${id}-header-menu`,
                    iconName: 'more-horizontal',
                    title: 'More options'
                },
                icons: true,
                style,
                variant: 'secondary'
            },
            menuItems
        );

    return rc(
        HeaderButtons,
        null,
        headerButtons.map(button => rc(FormHeaderButton, {
            ...button.props, key: button.key || button.props.hNode.id,
            hNode: { ...button.props.hNode, displayTitleOnButton: false },
            title: button.props.hNode.title, style, variant: 'secondary', size: 'sm'
        })),
        menuButton,
        cancelButton &&
            isValidElement(cancelButton) &&
            rc(
                FormHeaderButton,
                (function () {
                    /** @type {any} */
                    const cbProps = cancelButton.props || {};
                    const cbHNode = cbProps.hNode || {};
                    return {
                        ...cbProps,
                        hNode: {
                            ...cbHNode,
                            title: '',
                            iconName: 'x'
                        },
                        alt: 'Close form',
                        style,
                        variant: 'secondary'
                    };
                })()
            )
    );
}

function createSimpleTitleElement(title) {
    return rc(Title, { name: 'form-title', noPadding: true }, title);
}

function createTitleElement(hNode, record, mobile, dialogHasTitle) {
    if (!hNode) {
        return rc(View);
    }
    if (dialogHasTitle) {
        // A parent dialog already renders a title, so skip generating
        // one here to avoid duplication. Return empty View (not null)
        // to preserve FormHeader layout (keeps buttons right-aligned).
        return rc(View);
    }
    if (hNode.hNodeType === 'SingleRecordForm' && !hNode.title) {
        // Single record forms are untitled by default (they historically lived in dialogs,
        // which supplied the heading). When one is hosted directly in a detail pane it can
        // opt into a heading by setting an explicit title on the hNode (e.g. the
        // Serialized-Each "Create With Auto Serialization" / "Create From RFID Tag" panes).
        return rc(View);
    }
    if (hNode.title) {
        // Use the title from the hNode
        return createSimpleTitleElement(hNode.title);
    }

    let title = getPrettyRelationName(hNode.namespace, hNode.relation);

    if (!title) {
        return rc(View);
    }
    if (!mobile) {
        // per Figma, on desktop we show simple title
        return createSimpleTitleElement(title);
    }

    const titularProperty = metadata.getTitleAlternative(hNode.namespace, hNode.relation, 'assetNo');
    if (!titularProperty || !record[titularProperty]) {
        return rc(View);
    }

    return rc(
        View,
        { style: { flexDirection: 'column' } },
        rc(MobileTitle, null, title),
        rc(AssetNo, null, `${record[titularProperty]}`)
    );
}

function aggregateErrors(fieldErrors) {
    if (typeof fieldErrors === 'string') {
        return [fieldErrors];
    }
    if (Array.isArray(fieldErrors)) {
        return fieldErrors
            .filter(e => e) // Remove nullish errors.
            .flatMap(err => aggregateErrors(err));
    }
    return Object.values(fieldErrors)
        .filter(e => e) // Remove nullish errors.
        .flatMap(err => aggregateErrors(err));
}

// @ts-ignore
FormGL2025.defaultProps = {
    heightGreedy: true,
    centerFields: false
};

FormGL2025.propTypes = {
    hNode: propTypes.shape({
        title: propTypes.string,
        id: propTypes.string
    }).isRequired,
    heightGreedy: propTypes.bool,
    centerFields: propTypes.bool
};
FormGL2025._private = { aggregateErrors, createHeaderButtons, createTitleElement };
export default FormGL2025;
